import { Component, OnInit } from '@angular/core';
import { ConfigManagerService } from '@xpo-ltl/config-manager';
import { XpoSnackBar } from '@xpo-ltl/ngx-ltl-core/snack-bar';
import { FirebaseApiService } from '@xpo-ltl/sdk-firebase';
import firebase from 'firebase/app';
import 'firebase/auth';
import { from, Observable, of, throwError } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { ConfigManagerProperties } from '../shared/enums/config-manager-properties.enum';

/**
 * Firebase Storage REST host. Requests are authenticated with the signed-in user's
 * Firebase ID token (`Authorization: Firebase <idToken>`) - the same mechanism the
 * Firebase SDK uses internally - instead of a long-lived download token in the URL.
 */
const FIREBASE_STORAGE_HOST = 'https://firebasestorage.googleapis.com';

/** How long a blob: URL handed to a new tab stays valid before it is revoked. */
const BLOB_URL_TTL_MS = 60000;

@Component({
  selector: 'app-sic',
  templateUrl: './sic.component.html',
  styleUrls: ['./sic.component.scss'],
})
export class SicComponent implements OnInit {
  env: boolean = false;
  sicCode: string = '';
  private readonly bucketName: string;

  constructor(
    private configManagerService: ConfigManagerService,
    private snackBar: XpoSnackBar,
    private firebaseApiService: FirebaseApiService
  ) {
    const isProd: boolean = this.configManagerService.getSetting<boolean>(ConfigManagerProperties.production);
    const project: string = isProd ? 'xpo-ltl-prd' : 'xpo-ltl-dev';
    const region: string = this.configManagerService.getSetting<string>(ConfigManagerProperties.region);
    this.bucketName = `${project}-${region.toLowerCase()}-pdcs-bch-weeklypwdpdf`;
  }

  ngOnInit(): void {
    this.firebaseApiService.loggedInUser().subscribe((user) => {
      this.sicCode = user.sicCode;
    });
    this.env = this.configManagerService.getSetting<boolean>(ConfigManagerProperties.production);
  }

  download(sicCode: string): void {
    if (!sicCode || sicCode.length !== 3) {
      this.snackBar.warn('Please Enter a Valid SIC.');
      return;
    }

    const sic = sicCode.toUpperCase();

    this.currentIdToken()
      .pipe(
        switchMap((idToken) => {
          if (!idToken) {
            return throwError(new Error('NO_ID_TOKEN'));
          }
          return from(fetch(this.objectUrl(sic), { headers: { Authorization: `Firebase ${idToken}` } }));
        }),
        switchMap((response) => this.toBlob(response))
      )
      .subscribe(
        (blob) => this.openInNewTab(blob, sic),
        (error: Error) => this.snackBar.error(this.errorMessage(error, sic))
      );
  }

  /**
   * ID token of the user that @xpo-ltl/ngx-auth signed into the default Firebase app
   * (via signInWithCustomToken in XpoLtlAuthGuard); null if nobody is signed in.
   */
  private currentIdToken(): Observable<string | null> {
    const user = firebase.auth().currentUser;
    return user ? from(user.getIdToken()) : of(null);
  }

  /** Token-less object URL, encoded the same way the Firebase SDK builds its REST paths. */
  private objectUrl(objectPath: string): string {
    return `${FIREBASE_STORAGE_HOST}/v0/b/${encodeURIComponent(this.bucketName)}/o/${encodeURIComponent(
      objectPath
    )}?alt=media`;
  }

  /** Reads the body as a blob; the objects are stored as application/pdf, so the type carries over. */
  private toBlob(response: Response): Observable<Blob> {
    if (!response.ok) {
      return throwError(new Error(`HTTP_${response.status}`));
    }
    return from(response.blob());
  }

  private errorMessage(error: Error, sic: string): string {
    switch (error && error.message) {
      case 'NO_ID_TOKEN':
        return 'Your session has expired. Please refresh and sign in again.';
      case 'HTTP_404':
        return `BRP File is not available for SIC: ${sic}`;
      case 'HTTP_401':
      case 'HTTP_403':
        return `You are not authorized to download the BRP file for SIC: ${sic}`;
      default:
        return `Unable to download BRP file for SIC: ${sic}. Please try again.`;
    }
  }

  /**
   * Opens the fetched PDF in a new tab via a blob: URL. That URL is local to this browser
   * session and carries no credentials; it is released once the new tab has had time to load.
   */
  private openInNewTab(blob: Blob, sic: string): void {
    const objectUrl = URL.createObjectURL(blob);
    const tab = window.open(objectUrl, '_blank');
    if (!tab) {
      URL.revokeObjectURL(objectUrl);
      this.snackBar.error(`Pop-up blocked. Please allow pop-ups for this site to view the BRP file for SIC: ${sic}`);
      return;
    }
    setTimeout(() => URL.revokeObjectURL(objectUrl), BLOB_URL_TTL_MS);
  }
}
